'use client';

import useSimpleApiHandler from '@frontend/jetlend-core/src/hooks/useSimpleApiHandler';
import React, { useMemo } from 'react';
import {
    isMobile,
    useWindowSize,
} from '@frontend/jetlend-web-ui/src/utils/responsive';
import Section from '@app/ui/Section/Section';
import AchievementBlock from './AchievementBlock/AchievementBlock';
import CoursesBlock from './CoursesBlock/CoursesBlock';
import { getUserCoursesHandler } from '@app/ducks/academy/course';
import CoursesSection from '../../courses/CoursesSection';
import {
    IAchievementItemApiModel,
    ICourseInformationApiModel,
} from '@app/models/features/course';

import styles from './MainSection.module.scss';

export interface IProps {
    /**
     * Список ачивок
     */
    achievements: IAchievementItemApiModel[];
    /**
     * Список всех курсов
     */
    courses: ICourseInformationApiModel[];
}

/**
 * Компонент для отрисовки середины страницы академии
 */
const MainSection: React.FC<IProps> = props => {
    const {
        achievements,
        courses,
    } = props;
    const [userCourses] = useSimpleApiHandler(getUserCoursesHandler);
    const { width } = useWindowSize();
    const isMobileScreen = isMobile(width);

    const achievementsCount = achievements?.length;
    const userAchievements = useMemo(() => achievements?.filter(achievement => achievement?.achieved), [achievements]);
    const userAchievementsCount = userAchievements?.length;

    // Исключаем из состояния курсы, которые были уже удалены или потерялись
    const existingUserCourses = useMemo(() => {
        if (!userCourses || userCourses.length === 0) {
            return [];
        }

        const coursesMap = courses.reduce((acc, courseState) => {
            acc[courseState.id] = courseState;
            return acc;
        }, {});

        return userCourses.filter(userCourse => coursesMap[userCourse.id]);
    }, [ courses, userCourses ]);

    return (
        <>
            {existingUserCourses && existingUserCourses?.length > 0 && (
                <Section title="Пройденные курсы" animation={false}>
                    {courses && courses?.length > 0 && (
                        <CoursesBlock
                            courses={courses}
                            achievements={achievements}
                            states={existingUserCourses}
                        />
                    )}
                </Section>
            )}
            {achievementsCount > 0 && (
                <Section
                    title="Мои награды"
                    className={styles['awards-container']}
                    action={(
                        <span>
                            Получено:&nbsp;
                            <span className={styles['action--green']}>{userAchievementsCount} из {achievementsCount}</span>
                        </span>
                    )}
                    animation={false}
                >
                    <AchievementBlock achievements={achievements} />
                </Section>
            )}
            {achievements && courses && (
                <Section animation={false} stretch={isMobileScreen ? 'horizontally' : undefined}>
                    <CoursesSection achievements={achievements} courses={courses} />
                </Section>
            )}
        </>
    );
};

export default MainSection;